import { Footnote, Spacing, Text } from '@vkontakte/vkui';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    subscriptionExpiredInformerEnabled,
    subsExpiredInformerStorageKey,
} from 'reactApp/appHelpers/featuresHelpers/features/subscriptionsInformer';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import { useGoToSubscriptions } from 'reactApp/hooks/useGoToSubscriptions';
import { openExpiredSubsModal } from 'reactApp/modules/payment/payment.module';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { Button } from 'reactApp/ui/Button/Button';
import { Informer } from 'reactApp/ui/Informer/Informer';
import { EInformerColor } from 'reactApp/ui/Informer/Informer.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './HasExpiredSubscriptionInformer.css';

interface HasExpiredSubscriptionInformerProps {
    productId: string;
}

export const HasExpiredSubscriptionInformer = memo(({ productId }: HasExpiredSubscriptionInformerProps): ReactElement | null => {
    const dispatch = useDispatch();
    const { goToSubscriptions } = useGoToSubscriptions();

    const [showInformer, setShowInformer] = useState<boolean>(true);

    const sendAnalytics = useCallback((action) => {
        sendPaymentGa({
            eventCategory: ECategoryGa.banner,
            action,
            name: 'mnz_recover_subs',
        });
    }, []);

    const onClose = useCallback(() => {
        sendAnalytics('close');
        storeHelper.markAsShown(subsExpiredInformerStorageKey);
        setShowInformer(false);
    }, [sendAnalytics]);

    const onClick = useCallback(() => {
        sendAnalytics('click');
        storeHelper.markAsShown(subsExpiredInformerStorageKey);
        setShowInformer(false);

        if (subscriptionExpiredInformerEnabled === 'c') {
            goToSubscriptions({
                isNewTariffsPage: true,
                query: PaymentUTM.subsRecoverBanner,
            });
            return;
        }

        dispatch(
            openExpiredSubsModal({
                productId,
                onFailProduct: () => {
                    goToSubscriptions({
                        isNewTariffsPage: true,
                        query: PaymentUTM.subsRecoverBanner,
                    });
                },
            })
        );
    }, [goToSubscriptions, productId, dispatch, sendAnalytics]);

    const button = useMemo(
        () => (
            <Button theme="vk" className={styles.button} onClick={onClick}>
                <Text weight="2"> Вернуть подписку</Text>
            </Button>
        ),
        []
    );

    useEffect(() => {
        sendAnalytics('view');
    }, []);

    if (!showInformer) {
        return null;
    }

    return (
        <div className={styles.root}>
            <Informer
                dataQaId="informer-trash-overq"
                color={EInformerColor.blue}
                button={button}
                closable={subscriptionExpiredInformerEnabled !== 'c'}
                onClose={onClose}
            >
                <div>
                    <Text weight="2">Возможности подписки Mail&nbsp;Space исчезли&nbsp;— но&nbsp;их&nbsp;можно вернуть</Text>
                    <Spacing size={4} />
                    <Footnote>Всё&nbsp;будет как&nbsp;раньше: много места и&nbsp;никакой рекламы даже&nbsp;в&nbsp;Почте</Footnote>
                </div>
            </Informer>
        </div>
    );
});

HasExpiredSubscriptionInformer.displayName = 'HasExpiredSubscriptionInformer';
